import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import memphisImg from '../assets/City_Mark_Color-01.png';
import equitySvg from '../assets/Equity.svg';
import livabilitySvg from '../assets/Livability.svg';
import economySvg from '../assets/economy.svg';
import environmentSvg from '../assets/Environment.svg';

import PDFMetricCard from "../components/pdf/PDFMetricCard";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { useContext, useEffect, useState } from "react";
import { categorizeData, getIndexMetricDataPDF, getMetricGroupChartId, getSortedMetrics, getSvgMap, orgId } from "../utils/dashboardUtils";
import LoadingIndicator from "../components/LoadingIndicator";
import TitleBanner from "../components/pdf/TitleBanner";
import DisaggregateMetricCard from "../components/DisaggregateMetricCard";
import PDFBarChart from "../components/pdf/PDFBarChart";
import PDFRaceEthnicityChart from "../components/pdf/PDFRaceEthnicityChart";
import { indicatorsEnv } from "../utils/environment";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    metadata: MetricMetadata[] | null;
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
    selectionActive: boolean;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, metadata, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, siteUrl, selectedArea, selectionActive }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('data:', disaggregatedData);

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    // Get sorted metrics for each dashboard panel
    const sortedDemographicMetrics: DisaggregatedMetric[][] = getSortedMetrics('Demographic', categorizedData);
    const sortedLivabilityMetrics: DisaggregatedMetric[][] = getSortedMetrics('Livability', categorizedData);
    const sortedLandUseMetrics: DisaggregatedMetric[][] = getSortedMetrics('Land use', categorizedData);

    // Get index metric data for each dashboard panel
    const indexMetricDataDemographic = getIndexMetricDataPDF('Demographic', metadata, disaggregatedData);
    const indexMetricDataLivability = getIndexMetricDataPDF('Livability', metadata, disaggregatedData);
    const indexMetricDataEconomy = getIndexMetricDataPDF('Economy', metadata, disaggregatedData);
    const indexMetricDataLandUse = getIndexMetricDataPDF('Land use', metadata, disaggregatedData);

    // Check if city lod Population metric is present, and filter out block group lod
    const flatDemographicMetrics = sortedDemographicMetrics.flat();

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    {/* Print and Close buttons */}
                    <div className="flex justify-center items-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-[#0A5F75] border border-[#0A5F75] text-white py-1 pl-7 pr-8 rounded-md"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-3 h-3 mr-2 mt-[0.5px]" />
                            <p className="text-xs font-bold">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-[#0A5F75] py-1 px-8 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-xs text-[#0A5F75] font-bold">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative">
                            <div className="w-full h-auto p-4 flex justify-between items-center bg-white border border-gray-100 rounded-md shadow-md">
                                <img src={memphisImg} alt='City of Memphis Logo' className="w-32 h-auto"/>
                                <div className="flex flex-col items-start justify-start mx-4 w-full">
                                    <p className="text-lacey-text font-bold text-[18px]">City of Memphis, TN</p>
                                    <p className="text-xs text-lacey-text">{selectedArea || 'City of Memphis'}</p>
                                    <p className="text-xs text-lacey-text font-bold">{siteUrl || ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="h-10 w-30 mr-8 mt-4" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        {/* Demographic Metrics */}
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Equity"/>
                                <p className="text-lacey-text mx-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {flatDemographicMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}        
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Demographic Charts */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Equity"/>
                                <div className="grid grid-cols-2 gap-2 place-items-start mx-4 mt-4">
                                    <div className="col-span-1 w-full px-2 my-4">
                                        {categorizedData.Demographic.filter(metric =>  
                                            [getMetricGroupChartId('Demographic', "hhincome")].includes(metric.id) // Household Income
                                        ).map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                title={metric.name}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                        ))}
                                        <PDFBarChart indicators={disaggregatedData} title="Household Income" tab={'Demographic'} chartName="hhincome" height="h-56" barColor="#00A18A" reverseOrder={false} topFive={true}/>
                                    </div> 
                                    <div className="col-span-1 w-full px-2 my-4">
                                        {categorizedData.Demographic.filter(metric => [getMetricGroupChartId("Demographic", "race")].includes(metric.id))
                                        .map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                title={"Race & Ethnicity"}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                        ))}
                                        <PDFRaceEthnicityChart indicators={disaggregatedData} title="Race and Ethnicity" tab="Demographic" chartName="race" height="h-80" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 3: Livability */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataLivability} geoDetails={geoDetails} geoTag={geoTag} svg={livabilitySvg} text="Livability"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    {sortedLivabilityMetrics.flat().map((metric) => (
                                        <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgMap(metric.id)}
                                        showMetadataIcon={false}
                                        disaggData={disaggregatedData}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 4: Economy */}
                    <div className="page">
                        <div className="content">
                            <div className="shadow-md rounded-md my-8">
                                <TitleBanner metric={indexMetricDataEconomy} geoDetails={geoDetails} geoTag={geoTag} svg={economySvg} text="Economy"/>
                                <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                    <div className="col-span-2 col-start-2 w-full px-2 my-4">
                                        {categorizedData.Economy.filter(metric => [getMetricGroupChartId("Economy", "industry")].includes(metric.id))
                                        .map(metric => (
                                            <DisaggregateMetricCard
                                                key={metric.id}
                                                id={metric.id}
                                                title={metric.name}
                                                svg={getSvgMap(metric.id)}
                                                disaggData={disaggregatedData}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                showMetadataIcon={false}
                                            />
                                        ))}
                                        <PDFBarChart indicators={disaggregatedData} title="Employment By Industry" tab="Economy" chartName="industry" height="h-60" barColor="#00A18A" reverseOrder={false} topFive={true}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 5: Land use */}
                    <div className="page">
                        <div className="shadow-md rounded-md my-8">
                            <TitleBanner metric={indexMetricDataLandUse} geoDetails={geoDetails} geoTag={geoTag} svg={environmentSvg} text="Land use"/>
                            <div className="grid grid-cols-4 gap-2 place-items-start mx-4 mt-4">
                                {sortedLandUseMetrics.flat().map((metric) => (
                                    <PDFMetricCard
                                    key={metric.id}
                                    metric={metric}
                                    width="w-full"
                                    svg={getSvgMap(metric.id)}
                                    showMetadataIcon={false}
                                    disaggData={disaggregatedData}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;